import { DashboardHeader } from '@/components/dashboard/dashboard-header'
import { DashboardShell } from '@/components/dashboard/dashboard-shell'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useTRPC } from '@/trpc/react'
import { isGuestOrIsLocalDevBusiness } from '@repo/shared_utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Calculator, Heart, Home, Info, Pencil, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

export const Route = createFileRoute('/dashboard/admin/government_contributions/')({
  component: RouteComponent,
})

interface SSSContributionBracket {
  bracketId: string
  rangeStart: number
  rangeEnd: number
  employeeContributionAmount: number
  businessId: string
  // employerContributionAmount: number | null
  effectiveStartDate: string | null
  effectiveEndDate: string | null
  createdBy: string | null
  createdAt: Date
  updatedAt: Date
}

interface NewBracketForm {
  rangeStart: number
  rangeEnd: number
  employeeContributionAmount: number
}

interface NewPhilhealthBracketForm {
  rangeStart: number
  rangeEnd: number
  employeeContributionPercentage: number
  minAmount: number
  maxAmount: number
}

function RouteComponent() {
  const api = useTRPC()
  const queryClient = useQueryClient()

  // SSS states
  const [editingSssBracket, setEditingSssBracket] = useState<SSSContributionBracket | null>(null)
  const [sssBracketToDelete, setSssBracketToDelete] = useState<string | null>(null)
  const [newSssBracket, setNewSssBracket] = useState<NewBracketForm>({
    rangeStart: 0,
    rangeEnd: 0,
    employeeContributionAmount: 0,
  })
  const [isAddingSssBracket, setIsAddingSssBracket] = useState(false)

  // HDMF states
  const [isAddingHdmfBracket, setIsAddingHdmfBracket] = useState(false)
  const [editingHdmfBracket, setEditingHdmfBracket] = useState<SSSContributionBracket | null>(null)
  const [hdmfBracketToDelete, setHdmfBracketToDelete] = useState<string | null>(null)
  const [newHdmfBracket, setNewHdmfBracket] = useState<NewBracketForm>({
    rangeStart: 0,
    rangeEnd: 0,
    employeeContributionAmount: 0,
  })

  // Philhealth states
  const [isAddingPhilhealthBracket, setIsAddingPhilhealthBracket] = useState(false)
  const [editingPhilhealthBracket, setEditingPhilhealthBracket] = useState<any | null>(null)
  const [philhealthBracketToDelete, setPhilhealthBracketToDelete] = useState<string | null>(null)
  const [newPhilhealthBracket, setNewPhilhealthBracket] = useState<NewPhilhealthBracketForm>({
    rangeStart: 0,
    rangeEnd: 0,
    employeeContributionPercentage: 0,
    minAmount: 0,
    maxAmount: 0,
  })

  // Tax states
  const [isAddingTaxBracket, setIsAddingTaxBracket] = useState(false)
  const [editingTaxBracket, setEditingTaxBracket] = useState<any | null>(null)
  const [taxBracketToDelete, setTaxBracketToDelete] = useState<string | null>(null)
  const [newTaxBracket, setNewTaxBracket] = useState<{
    rangeStart: number
    rangeEnd: number
    taxRate: number
    additionalTax: number
  }>({
    rangeStart: 0,
    rangeEnd: 0,
    taxRate: 0,
    additionalTax: 0,
  })

  // query hooks for SSS
  const { data: sssContributionBrackets } = useQuery({
    ...api.sss.getSSSContributionBrackets.queryOptions(),
  })
  const { mutate: createSssContributionBracket } = useMutation({
    ...api.sss.createSSSContributionBracket.mutationOptions(),
  })
  const { mutate: editSssContributionBracket } = useMutation({
    ...api.sss.editSSSContributionBracket.mutationOptions(),
  })
  const { mutate: deleteSssContributionBracket } = useMutation({
    ...api.sss.deleteSSSContributionBracket.mutationOptions(),
  })

  // handle functions for SSS
  const handleAddSssBracket = () => {
    // Validate the input values
    if (
      newSssBracket.rangeStart < 0 ||
      newSssBracket.rangeEnd < 0 ||
      newSssBracket.employeeContributionAmount < 0
    ) {
      toast.error('All values must be positive numbers')
      return
    }

    if (newSssBracket.rangeStart >= newSssBracket.rangeEnd) {
      toast.error('Range end must be greater than range start')
      return
    }

    createSssContributionBracket(
      {
        rangeStart: newSssBracket.rangeStart.toString(),
        rangeEnd: newSssBracket.rangeEnd.toString(),
        employeeContributionAmount: newSssBracket.employeeContributionAmount.toString(),
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: api.sss.getSSSContributionBrackets.queryKey(),
          })
          toast.success('Successfully Created a new SSS bracket.')
        },
        onError: (error) => {
          console.error('Error creating sss contribution bracket:', error)
          toast.error('Failed to create new SSS bracket.')
        },
      },
    )

    // Reset form
    setNewSssBracket({
      rangeStart: 0,
      rangeEnd: 0,
      employeeContributionAmount: 0,
    })
    setIsAddingSssBracket(false)
  }

  const handleEditSssBracket = (bracket: SSSContributionBracket) => {
    setEditingSssBracket(bracket)
    setNewSssBracket({
      rangeStart: bracket.rangeStart,
      rangeEnd: bracket.rangeEnd,
      employeeContributionAmount: bracket.employeeContributionAmount,
    })
    setIsAddingSssBracket(true)
  }

  const handleUpdateSssBracket = () => {
    if (!editingSssBracket) return
    console.log('handleUpdateBracket')
    console.log({
      editingBracket: editingSssBracket,
      newBracket: newSssBracket,
    })

    editSssContributionBracket(
      {
        bracketId: editingSssBracket.bracketId,
        rangeStart: newSssBracket.rangeStart.toString(),
        rangeEnd: newSssBracket.rangeEnd.toString(),
        employeeContributionAmount: newSssBracket.employeeContributionAmount.toString(),
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: api.sss.getSSSContributionBrackets.queryKey(),
          })
          toast.success('Successfully updated SSS bracket.')
        },
        onError: (error) => {
          console.error('Error updating sss contribution bracket:', error)
          toast.error('Failed to update SSS bracket.')
        },
      },
    )

    setEditingSssBracket(null)
    setNewSssBracket({
      rangeStart: 0,
      rangeEnd: 0,
      employeeContributionAmount: 0,
    })
    setIsAddingSssBracket(false)
  }

  const handleDeleteSssBracket = (bracketId: string) => {
    console.log('handleDeleteBracket', bracketId)
    deleteSssContributionBracket(
      {
        bracketId,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: api.sss.getSSSContributionBrackets.queryKey(),
          })
          toast.success('Successfully deleted SSS bracket.')
        },
        onError: (error) => {
          console.error('Error deleting sss contribution bracket:', error)
          toast.error('Failed to delete SSS bracket.')
        },
      },
    )
    setSssBracketToDelete(null)
  }

  // HDMF
  const { data: hdmfContributionBrackets } = useQuery({
    ...api.hdmf.getHdmfContributionBrackets.queryOptions(),
  })
  const { mutate: createHdmfContributionBracket } = useMutation({
    ...api.hdmf.createHdmfContributionBracket.mutationOptions(),
  })
  const { mutate: editHdmfContributionBracket } = useMutation({
    ...api.hdmf.editHdmfContributionBracket.mutationOptions(),
  })
  const { mutate: deleteHdmfContributionBracket } = useMutation({
    ...api.hdmf.deleteHdmfContributionBracket.mutationOptions(),
  })

  // handle functions for HDMF
  const handleAddHdmfBracket = () => {
    // Validate the input values
    if (
      newHdmfBracket.rangeStart < 0 ||
      newHdmfBracket.rangeEnd < 0 ||
      newHdmfBracket.employeeContributionAmount < 0
    ) {
      toast.error('All values must be positive numbers')
      return
    }

    if (newHdmfBracket.rangeStart >= newHdmfBracket.rangeEnd) {
      toast.error('Range end must be greater than range start')
      return
    }

    createHdmfContributionBracket(
      {
        rangeStart: newHdmfBracket.rangeStart.toString(),
        rangeEnd: newHdmfBracket.rangeEnd.toString(),
        employeeContributionAmount: newHdmfBracket.employeeContributionAmount.toString(),
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: api.hdmf.getHdmfContributionBrackets.queryKey(),
          })
          toast.success('Successfully Created a new HDMF bracket.')
        },
        onError: (error) => {
          console.error('Error creating hdmf contribution bracket:', error)
          toast.error('Failed to create new HDMF bracket.')
        },
      },
    )

    // Reset form
    setNewHdmfBracket({
      rangeStart: 0,
      rangeEnd: 0,
      employeeContributionAmount: 0,
    })
    setIsAddingHdmfBracket(false)
  }

  const handleEditHdmfBracket = (bracket: SSSContributionBracket) => {
    setEditingHdmfBracket(bracket)
    setNewHdmfBracket({
      rangeStart: bracket.rangeStart,
      rangeEnd: bracket.rangeEnd,
      employeeContributionAmount: bracket.employeeContributionAmount,
    })
    setIsAddingHdmfBracket(true)
  }

  const handleUpdateHdmfBracket = () => {
    if (!editingHdmfBracket) return

    editHdmfContributionBracket(
      {
        bracketId: editingHdmfBracket.bracketId,
        rangeStart: newHdmfBracket.rangeStart.toString(),
        rangeEnd: newHdmfBracket.rangeEnd.toString(),
        employeeContributionAmount: newHdmfBracket.employeeContributionAmount.toString(),
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: api.hdmf.getHdmfContributionBrackets.queryKey(),
          })
          toast.success('Successfully updated HDMF bracket.')
        },
      },
    )
    setEditingHdmfBracket(null)
    setNewHdmfBracket({
      rangeStart: 0,
      rangeEnd: 0,
      employeeContributionAmount: 0,
    })
    setIsAddingHdmfBracket(false)
  }

  const handleDeleteHdmfBracket = (bracketId: string) => {
    deleteHdmfContributionBracket(
      {
        bracketId,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: api.hdmf.getHdmfContributionBrackets.queryKey(),
          })
          toast.success('Successfully deleted HDMF bracket.')
        },
        onError: (error) => {
          console.error('Error deleting hdmf contribution bracket:', error)
          toast.error('Failed to delete HDMF bracket.')
        },
      },
    )
    setHdmfBracketToDelete(null)
  }

  // Philhealth
  const { data: philhealthContributionBrackets } = useQuery({
    ...api.philhealth.getPhilhealthContributionBrackets.queryOptions(),
  })
  const { mutate: createPhilhealthContributionBracket } = useMutation({
    ...api.philhealth.createPhilhealthContributionBracket.mutationOptions(),
  })
  const { mutate: editPhilhealthContributionBracket } = useMutation({
    ...api.philhealth.editPhilhealthContributionBracket.mutationOptions(),
  })
  const { mutate: deletePhilhealthContributionBracket } = useMutation({
    ...api.philhealth.deletePhilhealthContributionBracket.mutationOptions(),
  })

  // handle functions for Philhealth
  const handleAddPhilhealthBracket = () => {
    // Validate the input values
    if (
      newPhilhealthBracket.rangeStart < 0 ||
      newPhilhealthBracket.rangeEnd < 0 ||
      newPhilhealthBracket.employeeContributionPercentage < 0 ||
      newPhilhealthBracket.minAmount < 0 ||
      newPhilhealthBracket.maxAmount < 0
    ) {
      toast.error('All values must be positive numbers')
      return
    }

    if (newPhilhealthBracket.rangeStart >= newPhilhealthBracket.rangeEnd) {
      toast.error('Range end must be greater than range start')
      return
    }

    if (newPhilhealthBracket.minAmount > newPhilhealthBracket.maxAmount) {
      toast.error('Maximum amount must be greater than or equal to minimum amount')
      return
    }

    createPhilhealthContributionBracket(
      {
        rangeStart: newPhilhealthBracket.rangeStart.toString(),
        rangeEnd: newPhilhealthBracket.rangeEnd.toString(),
        employeeContributionPercentage:
          newPhilhealthBracket.employeeContributionPercentage.toString(),
        minAmount: newPhilhealthBracket.minAmount.toString(),
        maxAmount: newPhilhealthBracket.maxAmount.toString(),
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: api.philhealth.getPhilhealthContributionBrackets.queryKey(),
          })
          toast.success('Successfully Created a new Philhealth bracket.')
        },
        onError: (error) => {
          console.error('Error creating philhealth contribution bracket:', error)
          toast.error('Failed to create new Philhealth bracket.')
        },
      },
    )
    // Reset form
    setNewPhilhealthBracket({
      rangeStart: 0,
      rangeEnd: 0,
      employeeContributionPercentage: 0,
      minAmount: 0,
      maxAmount: 0,
    })
    setIsAddingPhilhealthBracket(false)
  }

  const handleEditPhilhealthBracket = (bracket: any) => {
    setEditingPhilhealthBracket(bracket)
    setNewPhilhealthBracket({
      rangeStart: Number(bracket.rangeStart),
      rangeEnd: Number(bracket.rangeEnd),
      employeeContributionPercentage: Number(bracket.employeeContributionPercentage),
      minAmount: Number(bracket.minAmount),
      maxAmount: Number(bracket.maxAmount),
    })
    setIsAddingPhilhealthBracket(true)
  }

  const handleUpdatePhilhealthBracket = () => {
    if (!editingPhilhealthBracket) return

    editPhilhealthContributionBracket(
      {
        bracketId: editingPhilhealthBracket.bracketId,
        rangeStart: newPhilhealthBracket.rangeStart.toString(),
        rangeEnd: newPhilhealthBracket.rangeEnd.toString(),
        employeeContributionPercentage:
          newPhilhealthBracket.employeeContributionPercentage.toString(),
        minAmount: newPhilhealthBracket.minAmount.toString(),
        maxAmount: newPhilhealthBracket.maxAmount.toString(),
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: api.philhealth.getPhilhealthContributionBrackets.queryKey(),
          })
          toast.success('Successfully updated Philhealth bracket.')
        },
        onError: (error) => {
          console.error('Error updating philhealth contribution bracket:', error)
          toast.error('Failed to update Philhealth bracket.')
        },
      },
    )

    setEditingPhilhealthBracket(null)
    setNewPhilhealthBracket({
      rangeStart: 0,
      rangeEnd: 0,
      employeeContributionPercentage: 0,
      minAmount: 0,
      maxAmount: 0,
    })
    setIsAddingPhilhealthBracket(false)
  }

  const handleDeletePhilhealthBracket = (bracketId: string) => {
    deletePhilhealthContributionBracket({
      bracketId,
    })
    setPhilhealthBracketToDelete(null)
  }

  // Tax
  const { data: taxBrackets } = useQuery({
    ...api.tax.getTaxBrackets.queryOptions(),
  })
  const { mutate: createTaxBracket } = useMutation({
    ...api.tax.createTaxBracket.mutationOptions(),
  })
  const { mutate: editTaxBracket } = useMutation({
    ...api.tax.editTaxBracket.mutationOptions(),
  })
  const { mutate: deleteTaxBracket } = useMutation({
    ...api.tax.deleteTaxBracket.mutationOptions(),
  })

  // handle functions for Tax
  const handleAddTaxBracket = () => {
    // Validate the input values
    if (
      newTaxBracket.rangeStart < 0 ||
      newTaxBracket.rangeEnd < 0 ||
      newTaxBracket.taxRate < 0 ||
      newTaxBracket.additionalTax < 0
    ) {
      toast.error('All values must be positive numbers')
      return
    }

    if (newTaxBracket.rangeStart >= newTaxBracket.rangeEnd) {
      toast.error('Range end must be greater than range start')
      return
    }

    createTaxBracket(
      {
        rangeStart: newTaxBracket.rangeStart.toString(),
        rangeEnd: newTaxBracket.rangeEnd.toString(),
        taxRate: (newTaxBracket.taxRate / 100).toString(), // Convert percentage to decimal
        additionalTax: newTaxBracket.additionalTax.toString(),
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: api.tax.getTaxBrackets.queryKey(),
          })
          toast.success('Successfully Created a new Tax bracket.')
        },
        onError: (error) => {
          console.error('Error creating tax contribution bracket:', error)
          toast.error('Failed to create new Tax bracket.')
        },
      },
    )
    // Reset form
    setNewTaxBracket({
      rangeStart: 0,
      rangeEnd: 0,
      taxRate: 0,
      additionalTax: 0,
    })
    setIsAddingTaxBracket(false)
  }

  const handleEditTaxBracket = (bracket: any) => {
    setEditingTaxBracket(bracket)
    setNewTaxBracket({
      rangeStart: Number(bracket.rangeStart),
      rangeEnd: Number(bracket.rangeEnd),
      taxRate: Number(bracket.taxRate) * 100, // Convert decimal to percentage
      additionalTax: Number(bracket.additionalTax),
    })
    setIsAddingTaxBracket(true)
  }

  const handleUpdateTaxBracket = () => {
    if (!editingTaxBracket) return

    editTaxBracket(
      {
        bracketId: editingTaxBracket.bracketId,
        rangeStart: newTaxBracket.rangeStart.toString(),
        rangeEnd: newTaxBracket.rangeEnd.toString(),
        taxRate: (newTaxBracket.taxRate / 100).toString(), // Convert percentage to decimal
        additionalTax: newTaxBracket.additionalTax.toString(),
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: api.tax.getTaxBrackets.queryKey(),
          })
          toast.success('Successfully updated Tax bracket.')
        },
        onError: (error) => {
          console.error('Error updating tax contribution bracket:', error)
          toast.error('Failed to update Tax bracket.')
        },
      },
    )
    setEditingTaxBracket(null)
    setNewTaxBracket({
      rangeStart: 0,
      rangeEnd: 0,
      taxRate: 0,
      additionalTax: 0,
    })
    setIsAddingTaxBracket(false)
  }

  const handleDeleteTaxBracket = (bracketId: string) => {
    deleteTaxBracket({
      bracketId,
    })
    setTaxBracketToDelete(null)
  }

  return (
    <DashboardShell>
      <DashboardHeader
        heading="Government Contributions"
        text="Configure settings for government-mandated contributions and tax calculations."
      />
      <div className="grid gap-4">
        {/* SSS Contributions Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calculator className="h-4 w-4" />
                <CardTitle className="text-lg">SSS Contribution Table</CardTitle>
              </div>
              <Button onClick={() => setIsAddingSssBracket(true)} disabled={false}>
                <Plus className="h-4 w-4 mr-2" /> Add New Bracket
              </Button>
            </div>
            <CardDescription>
              Social Security System contribution brackets and rates
            </CardDescription>
          </CardHeader>
          <CardContent>
            {(isAddingSssBracket || editingSssBracket) && (
              <div className="mb-4 p-4 border rounded-md bg-muted">
                <h2 className="text-lg font-semibold mb-2">
                  {editingSssBracket ? 'Edit Bracket' : 'Add New Bracket'}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                  <Input
                    type="number"
                    placeholder="Range Start"
                    value={newSssBracket.rangeStart}
                    onChange={(e) =>
                      setNewSssBracket({ ...newSssBracket, rangeStart: Number(e.target.value) })
                    }
                  />
                  <Input
                    type="number"
                    placeholder="Range End"
                    value={newSssBracket.rangeEnd}
                    onChange={(e) =>
                      setNewSssBracket({ ...newSssBracket, rangeEnd: Number(e.target.value) })
                    }
                  />
                  <Input
                    type="number"
                    placeholder="Employee Share"
                    value={newSssBracket.employeeContributionAmount}
                    onChange={(e) =>
                      setNewSssBracket({
                        ...newSssBracket,
                        employeeContributionAmount: Number(e.target.value),
                      })
                    }
                  />
                  <div className="flex gap-2">
                    <Button
                      onClick={editingSssBracket ? handleUpdateSssBracket : handleAddSssBracket}
                    >
                      {editingSssBracket ? 'Update' : 'Add'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsAddingSssBracket(false)
                        setEditingSssBracket(null)
                        setNewSssBracket({
                          rangeStart: 0,
                          rangeEnd: 0,
                          employeeContributionAmount: 0,
                        })
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            )}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Monthly Range Start (₱)</TableHead>
                  <TableHead>Monthly Range End (₱)</TableHead>
                  <TableHead>Monthly Employee Share (₱)</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sssContributionBrackets?.map((bracket) => {
                  const rangeStart = Number(bracket.rangeStart)
                  const rangeEnd = Number(bracket.rangeEnd)
                  const employeeContributionAmount = Number(bracket.employeeContributionAmount)

                  return (
                    <TableRow key={bracket.bracketId}>
                      <TableCell>{rangeStart.toLocaleString()}</TableCell>
                      <TableCell>{rangeEnd.toLocaleString()}</TableCell>
                      <TableCell>{employeeContributionAmount.toLocaleString()}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() =>
                              handleEditSssBracket({
                                ...bracket,
                                rangeStart,
                                rangeEnd,
                                employeeContributionAmount,
                              })
                            }
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setSssBracketToDelete(bracket.bracketId)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>

            {/* Delete Confirmation Dialog */}
            <AlertDialog
              open={sssBracketToDelete !== null}
              onOpenChange={(open) => !open && setSssBracketToDelete(null)}
            >
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete SSS Contribution Bracket</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete this SSS contribution bracket? This action
                    cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() =>
                      sssBracketToDelete && handleDeleteSssBracket(sssBracketToDelete)
                    }
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent>
        </Card>

        {/* HDMF Contributions Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Home className="h-4 w-4" />
                <CardTitle className="text-lg">HDMF Contribution Table</CardTitle>
              </div>
              <Button onClick={() => setIsAddingHdmfBracket(true)} disabled={false}>
                <Plus className="h-4 w-4 mr-2" /> Add New Bracket
              </Button>
            </div>
            <CardDescription>
              Home Development Mutual Fund (Pag-IBIG) contribution brackets and rates
            </CardDescription>
          </CardHeader>
          <CardContent>
            {(isAddingHdmfBracket || editingHdmfBracket) && (
              <div className="mb-4 p-4 border rounded-md bg-muted">
                <h2 className="text-lg font-semibold mb-2">
                  {editingHdmfBracket ? 'Edit Bracket' : 'Add New Bracket'}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                  <Input
                    type="number"
                    placeholder="Range Start"
                    value={newHdmfBracket.rangeStart}
                    onChange={(e) =>
                      setNewHdmfBracket({ ...newHdmfBracket, rangeStart: Number(e.target.value) })
                    }
                  />
                  <Input
                    type="number"
                    placeholder="Range End"
                    value={newHdmfBracket.rangeEnd}
                    onChange={(e) =>
                      setNewHdmfBracket({ ...newHdmfBracket, rangeEnd: Number(e.target.value) })
                    }
                  />
                  <Input
                    type="number"
                    placeholder="Employee Share"
                    value={newHdmfBracket.employeeContributionAmount}
                    onChange={(e) =>
                      setNewHdmfBracket({
                        ...newHdmfBracket,
                        employeeContributionAmount: Number(e.target.value),
                      })
                    }
                  />
                  <div className="flex gap-2">
                    <Button
                      onClick={editingHdmfBracket ? handleUpdateHdmfBracket : handleAddHdmfBracket}
                    >
                      {editingHdmfBracket ? 'Update' : 'Add'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsAddingHdmfBracket(false)
                        setEditingHdmfBracket(null)
                        setNewHdmfBracket({
                          rangeStart: 0,
                          rangeEnd: 0,
                          employeeContributionAmount: 0,
                        })
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            )}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Monthly Range Start (₱)</TableHead>
                  <TableHead>Monthly Range End (₱)</TableHead>
                  <TableHead>Monthly Employee Share (₱)</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {hdmfContributionBrackets?.map((bracket) => {
                  const rangeStart = Number(bracket.rangeStart)
                  const rangeEnd = Number(bracket.rangeEnd)
                  const employeeContributionAmount = Number(bracket.employeeContributionAmount)

                  return (
                    <TableRow key={bracket.bracketId}>
                      <TableCell>{rangeStart.toLocaleString()}</TableCell>
                      <TableCell>{rangeEnd.toLocaleString()}</TableCell>
                      <TableCell>{employeeContributionAmount.toLocaleString()}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() =>
                              handleEditHdmfBracket({
                                ...bracket,
                                rangeStart,
                                rangeEnd,
                                employeeContributionAmount,
                              })
                            }
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setHdmfBracketToDelete(bracket.bracketId)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>

            {/* Delete Confirmation Dialog */}
            <AlertDialog
              open={hdmfBracketToDelete !== null}
              onOpenChange={(open) => !open && setHdmfBracketToDelete(null)}
            >
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete HDMF Contribution Bracket</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete this HDMF contribution bracket? This action
                    cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() =>
                      hdmfBracketToDelete && handleDeleteHdmfBracket(hdmfBracketToDelete)
                    }
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent>
        </Card>

        {/* Philhealth Contributions Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Heart className="h-4 w-4" />
                <CardTitle className="text-lg">PhilHealth Contribution Table</CardTitle>
              </div>
              <Button
                onClick={() => setIsAddingPhilhealthBracket(true)}
                disabled={isGuestOrIsLocalDevBusiness({
                  businessId: sssContributionBrackets?.[0]?.businessId ?? '',
                })}
              >
                <Plus className="h-4 w-4 mr-2" /> Add New Bracket
              </Button>
            </div>
            <CardDescription>
              {isGuestOrIsLocalDevBusiness({
                businessId: philhealthContributionBrackets?.[0]?.businessId ?? '',
              })
                ? 'Edit Amount Per Employee (go to employees page)'
                : 'Philippine Health Insurance Corporation contribution brackets and rates'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {(isAddingPhilhealthBracket || editingPhilhealthBracket) && (
              <div className="mb-4 p-4 border rounded-md bg-muted">
                <h2 className="text-lg font-semibold mb-2">
                  {editingPhilhealthBracket ? 'Edit Bracket' : 'Add New Bracket'}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                  <Input
                    type="number"
                    placeholder="Range Start"
                    value={newPhilhealthBracket.rangeStart}
                    onChange={(e) =>
                      setNewPhilhealthBracket({
                        ...newPhilhealthBracket,
                        rangeStart: Number(e.target.value),
                      })
                    }
                  />
                  <Input
                    type="number"
                    placeholder="Range End"
                    value={newPhilhealthBracket.rangeEnd}
                    onChange={(e) =>
                      setNewPhilhealthBracket({
                        ...newPhilhealthBracket,
                        rangeEnd: Number(e.target.value),
                      })
                    }
                  />
                  <Input
                    type="number"
                    placeholder="Employee Contribution Percentage"
                    value={newPhilhealthBracket.employeeContributionPercentage}
                    onChange={(e) =>
                      setNewPhilhealthBracket({
                        ...newPhilhealthBracket,
                        employeeContributionPercentage: Number(e.target.value),
                      })
                    }
                  />

                  <Input
                    type="number"
                    placeholder="Min Amount"
                    value={newPhilhealthBracket.minAmount}
                    onChange={(e) =>
                      setNewPhilhealthBracket({
                        ...newPhilhealthBracket,
                        minAmount: Number(e.target.value),
                      })
                    }
                  />
                  <Input
                    type="number"
                    placeholder="Max Amount"
                    value={newPhilhealthBracket.maxAmount}
                    onChange={(e) =>
                      setNewPhilhealthBracket({
                        ...newPhilhealthBracket,
                        maxAmount: Number(e.target.value),
                      })
                    }
                  />
                  <div className="flex gap-2">
                    <Button
                      onClick={
                        editingPhilhealthBracket
                          ? handleUpdatePhilhealthBracket
                          : handleAddPhilhealthBracket
                      }
                    >
                      {editingPhilhealthBracket ? 'Update' : 'Add'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsAddingPhilhealthBracket(false)
                        setEditingPhilhealthBracket(null)
                        setNewPhilhealthBracket({
                          rangeStart: 0,
                          rangeEnd: 0,
                          employeeContributionPercentage: 0,
                          minAmount: 0,
                          maxAmount: 0,
                        })
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            )}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Range Start (₱)</TableHead>
                  <TableHead>Range End (₱)</TableHead>
                  <TableHead>Monthly Employee Percentage (%)</TableHead>
                  <TableHead>Min Amount (₱)</TableHead>
                  <TableHead>Max Amount (₱)</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {philhealthContributionBrackets?.map((bracket) => {
                  const rangeStart = Number(bracket.rangeStart)
                  const rangeEnd = Number(bracket.rangeEnd)
                  const employeeContributionPercentage = Number(
                    bracket.employeeContributionPercentage,
                  )
                  const employerContributionPercentage = Number(
                    bracket.employerContributionPercentage,
                  )
                  const minAmount = Number(bracket.minAmount)
                  const maxAmount = Number(bracket.maxAmount)

                  return (
                    <TableRow key={bracket.bracketId}>
                      <TableCell>{rangeStart.toLocaleString()}</TableCell>
                      <TableCell>
                        {/* TODO: WTF IS THIS????*/}
                        {rangeEnd === 999999999 ? '60,000 and up' : rangeEnd.toLocaleString()}
                      </TableCell>
                      <TableCell>{employeeContributionPercentage.toFixed(1)}%</TableCell>
                      <TableCell>{minAmount.toLocaleString()}</TableCell>
                      <TableCell>{maxAmount.toLocaleString()}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() =>
                              handleEditPhilhealthBracket({
                                ...bracket,
                                rangeStart,
                                rangeEnd,
                                employeeContributionPercentage,
                                employerContributionPercentage,
                                minAmount,
                                maxAmount,
                              })
                            }
                            disabled={isGuestOrIsLocalDevBusiness({
                              businessId: philhealthContributionBrackets?.[0]?.businessId ?? '',
                            })}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setPhilhealthBracketToDelete(bracket.bracketId)}
                            disabled={isGuestOrIsLocalDevBusiness({
                              businessId: philhealthContributionBrackets?.[0]?.businessId ?? '',
                            })}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>

            {/* Delete Confirmation Dialog */}
            <AlertDialog
              open={philhealthBracketToDelete !== null}
              onOpenChange={(open) => !open && setPhilhealthBracketToDelete(null)}
            >
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Philhealth Contribution Bracket</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete this Philhealth contribution bracket? This
                    action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() =>
                      philhealthBracketToDelete &&
                      handleDeletePhilhealthBracket(philhealthBracketToDelete)
                    }
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent>
        </Card>

        {/* Tax Brackets Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calculator className="h-4 w-4" />
                <CardTitle className="text-lg">Tax Brackets(Yearly)</CardTitle>
              </div>
              <Button onClick={() => setIsAddingTaxBracket(true)} disabled={false}>
                <Plus className="h-4 w-4 mr-2" /> Add New Bracket
              </Button>
            </div>
            <CardDescription>Annual income tax brackets and rates</CardDescription>
          </CardHeader>
          <CardContent>
            {(isAddingTaxBracket || editingTaxBracket) && (
              <div className="mb-4 p-4 border rounded-md bg-muted">
                <h2 className="text-lg font-semibold mb-2">
                  {editingTaxBracket ? 'Edit Bracket' : 'Add New Bracket'}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                  <Input
                    type="number"
                    placeholder="Range Start"
                    value={newTaxBracket.rangeStart}
                    onChange={(e) =>
                      setNewTaxBracket({ ...newTaxBracket, rangeStart: Number(e.target.value) })
                    }
                  />
                  <Input
                    type="number"
                    placeholder="Range End"
                    value={newTaxBracket.rangeEnd}
                    onChange={(e) =>
                      setNewTaxBracket({ ...newTaxBracket, rangeEnd: Number(e.target.value) })
                    }
                  />
                  <Input
                    type="number"
                    placeholder="Tax Rate (%)"
                    value={newTaxBracket.taxRate}
                    onChange={(e) =>
                      setNewTaxBracket({ ...newTaxBracket, taxRate: Number(e.target.value) })
                    }
                  />
                  <Input
                    type="number"
                    placeholder="Additional Tax"
                    value={newTaxBracket.additionalTax}
                    onChange={(e) =>
                      setNewTaxBracket({ ...newTaxBracket, additionalTax: Number(e.target.value) })
                    }
                  />
                  <div className="flex gap-2">
                    <Button
                      onClick={editingTaxBracket ? handleUpdateTaxBracket : handleAddTaxBracket}
                    >
                      {editingTaxBracket ? 'Update' : 'Add'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsAddingTaxBracket(false)
                        setEditingTaxBracket(null)
                        setNewTaxBracket({
                          rangeStart: 0,
                          rangeEnd: 0,
                          taxRate: 0,
                          additionalTax: 0,
                        })
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            )}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Range Start (₱)</TableHead>
                  <TableHead>Range End (₱)</TableHead>
                  <TableHead>Tax Rate (Excess of Range Start) (%)</TableHead>
                  <TableHead>Additional Tax (₱)</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {taxBrackets?.map((bracket) => (
                  <TableRow key={bracket.bracketId}>
                    <TableCell>{bracket.rangeStart}</TableCell>
                    <TableCell>{bracket.rangeEnd}</TableCell>
                    <TableCell>{(Number(bracket.taxRate) * 100).toFixed(0)}%</TableCell>
                    <TableCell>{Number(bracket.additionalTax).toLocaleString()}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEditTaxBracket(bracket)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setTaxBracketToDelete(bracket.bracketId)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Delete Confirmation Dialog */}
            <AlertDialog
              open={taxBracketToDelete !== null}
              onOpenChange={(open) => !open && setTaxBracketToDelete(null)}
            >
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Tax Bracket</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete this tax bracket? This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() =>
                      taxBracketToDelete && handleDeleteTaxBracket(taxBracketToDelete)
                    }
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent>
        </Card>

        {/* Information Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Info className="h-4 w-4" />
              <CardTitle className="text-lg">Important Information</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <ul className="list-disc pl-4 space-y-2 text-sm text-muted-foreground">
              <li>
                SSS contributions are calculated based on an employee&apos;s monthly compensation.
              </li>
              <li>
                Tax brackets are applied on annual taxable income after all applicable deductions.
              </li>
              <li>
                The tax amount for each bracket includes the additional tax plus the rate applied
                to the excess over the minimum amount.
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </DashboardShell>
  )
}
